import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusChip } from "@/components/kpi/KpiCard";

function when(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Management alerts with "Acknowledge" (a note is required, so the next person knows what is being done). */
export function AlertList({ alerts, onChanged, onNavigate, compact = false, emptyText = "No open alerts. Everything is within its limits." }) {
  const [acking, setAcking] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (a) => {
    if (note.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/kpis/alerts/${a.id}/acknowledge`, { note: note.trim() });
      toast.success("Acknowledged");
      setAcking(null); setNote("");
      onChanged?.();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  if (!alerts.length) return <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="alerts-empty">{emptyText}</p>;
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="alert-list">
      {alerts.map((a) => (
        <li key={a.id} className={compact ? "px-4 py-3" : "px-4 py-3.5"} data-testid={`alert-${a.id}`}>
          <div className="flex items-start gap-3">
            <StatusChip status={a.level} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800 dark:text-slate-100">{a.message}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {a.project_code || a.company_name || "Organisation"} · raised {when(a.created_at)}
                {a.status === "ACKNOWLEDGED" && <> · acknowledged {when(a.acknowledged_at)}: “{a.acknowledged_note}”</>}
                {a.status === "RESOLVED" && <> · resolved {when(a.resolved_at)}</>}
              </p>
              {acking === a.id && (
                <div className="mt-2 space-y-2">
                  <Textarea autoFocus rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What is being done about it?"
                    aria-label="Acknowledgement note" data-testid="ack-note" />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy || note.trim().length < 2} onClick={() => submit(a)} data-testid="ack-submit">Acknowledge</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setAcking(null); setNote(""); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              {a.link && <Link to={a.link} onClick={onNavigate} className="text-xs font-medium text-[#910029] hover:underline dark:text-rose-300">Open</Link>}
              {a.status === "OPEN" && acking !== a.id && (
                <button type="button" onClick={() => { setAcking(a.id); setNote(""); }} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`ack-${a.id}`}>Acknowledge</button>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
